<template>
  <div>
    <v-card-title
      class="pb-0 text-h4 font-weight-bold"
      style="letter-spacing: 1px"
    >
      <v-icon color="primary" class="mr-2">mdi-file-document-multiple</v-icon>
      DOCUMENTS
    </v-card-title>
    <v-divider></v-divider>
    <v-card
      class="mt-3 pa-4 rounded-xl elevation-2"
      flat
      id="uploadedDocuments"
      style="background: #f5f7fa"
    >
      <!-- Confirmation Dialog -->
      <v-dialog
        v-model="dialogDeleteDoc"
        max-width="420px"
        transition="dialog-bottom-transition"
      >
        <v-card class="rounded-xl pa-4" style="background: #fff">
          <v-card-title class="justify-center">
            <v-avatar size="48" class="mb-2" color="red lighten-4">
              <v-icon color="red darken-2" size="32"
                >mdi-alert-circle-outline</v-icon
              >
            </v-avatar>
          </v-card-title>
          <v-card-title
            class="text-h5 font-weight-bold justify-center"
            style="color: #d32f2f"
          >
            Confirm Delete
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" class="d-flex align-center">
                <v-icon color="grey" class="mr-2">mdi-file</v-icon>
                <span><strong>File Name:</strong> {{ editedItem.name }}</span>
              </v-col>
              <v-col cols="12" class="d-flex align-center">
                <v-icon color="grey" class="mr-2">mdi-tag</v-icon>
                <span><strong>File Type:</strong> {{ editedItem.Type }}</span>
              </v-col>
              <v-col cols="12" class="d-flex align-center">
                <v-icon color="grey" class="mr-2">mdi-comment-text</v-icon>
                <span
                  ><strong>File Remark:</strong> {{ editedItem.Remark }}</span
                >
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="justify-center mt-2">
            <v-btn
              color="grey lighten-2"
              class="rounded-pill px-6"
              @click="closeDeleteDoc"
            >
              <v-icon left>mdi-close</v-icon> Cancel
            </v-btn>
            <v-btn
              color="red darken-2"
              class="rounded-pill px-6 ml-2 white--text"
              @click="deleteItemConfirm"
            >
              <v-icon left>mdi-trash-can-outline</v-icon> Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Upload Dialog -->
      <v-dialog
        v-model="dialogUploadDoc"
        max-width="540px"
        transition="dialog-bottom-transition"
      >
        <v-card class="rounded-xl pa-4" style="background: #fff">
          <v-card-title class="justify-center">
            <v-avatar size="48" color="primary lighten-4">
              <v-icon color="primary" size="32">mdi-cloud-upload</v-icon>
            </v-avatar>
          </v-card-title>
          <v-card-title
            class="justify-center text-h5 font-weight-bold"
            style="color: #1976d2"
          >
            {{ dialogTitle }}
          </v-card-title>
          <v-card-text>
            <div
              class="upload-drop-area"
              @dragover.prevent
              @drop.prevent="selectFile($event.dataTransfer.files[0])"
              @click="triggerFileInput"
            >
              <v-icon color="primary" size="48">mdi-cloud-upload</v-icon>
              <div v-if="!currentFile" class="mt-2" style="color: #1976d2">
                <strong>Drag & Drop or Click to Select Document</strong>
              </div>
              <div v-else class="mt-2 d-flex align-center justify-center">
                <v-icon color="success" size="32">mdi-file-check</v-icon>
                <span class="ml-2 font-weight-medium">{{
                  currentFile.name
                }}</span>
              </div>
              <input
                ref="fileInput"
                :key="fileInputKey"
                type="file"
                style="display: none"
                :accept="'image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'"
                @change="onFileInputChange"
              />
            </div>
            <v-alert
              v-if="currentFile && currentFile.size >= 4000000"
              type="error"
              dense
              class="mt-2"
            >
              File size should be less than 4 MB!
            </v-alert>
            <v-select
              suffix="required"
              placeholder="Select A Type"
              :items="selectTypes"
              v-model="uploads.Type"
              clearable
              class="mt-4 mb-2"
            ></v-select>
            <v-text-field
              v-model="uploads.Remark"
              label="Document Remark"
              clearable
              class="mb-2"
            ></v-text-field>
            <v-progress-linear
              v-if="progress > 0 && progress < 100"
              :value="progress"
              color="primary"
              height="6"
              rounded
              class="mb-2"
            ></v-progress-linear>
            <div class="d-flex align-center" :class="currentStageId === 3 ? 'justify-space-between' : 'justify-end'">
              <div v-if="currentStageId === 3" class="d-flex align-center mr-4">
                <div class="pa-3 rounded" style="border:2px solid #FF9800;background:#FFF3E0;max-width:320px">
                  <v-icon color="orange" small class="mr-1">mdi-alert-circle-outline</v-icon>
                  <span class="font-weight-medium orange--text text--darken-2" style="font-size:12px">I HAVE REVIEWED THE CLAIM AMOUNT AND IT IS IN ORDER</span>
                </div>
              </div>
              <div class="d-flex">
                <v-btn
                  small
                  outlined
                  color="grey"
                  class="rounded-pill"
                  @click="closeUploadDialog"
                >
                  <v-icon left>mdi-close</v-icon> Cancel
                </v-btn>
                <v-btn
                  small
                  color="primary"
                  class="rounded-pill ml-2"
                  :disabled="!currentFile || !uploads.Type"
                  @click="uploadDocConfirm(currentStageId)"
                >
                  <v-icon left>mdi-cloud-upload</v-icon> {{ currentStageId === 3 ? 'Approve' : 'Submit' }}
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Document Sections -->
      <div
        v-for="section in docSections"
        :key="section.id"
        :class="section.bg"
        class="mb-6 rounded-xl pa-4 elevation-1"
      >
        <v-row>
          <v-col
            :cols="section.id === 2 && claimTotal > 0 ? 4 : 6"
            class="d-flex align-center"
          >
            <v-avatar size="36" class="mr-2" :color="section.avatarColor">
              <v-icon :color="section.iconColor">{{ section.icon }}</v-icon>
            </v-avatar>
            <v-card-title
              class="justify-center text-h6 font-weight-bold"
              style="letter-spacing: 1px"
            >
              {{ section.title }}
            </v-card-title>
          </v-col>
          <v-col
            v-if="section.id === 2 && claimTotal > 0"
            cols="4"
            class="d-flex align-center justify-center"
          >
            <v-chip
              color="orange"
              dark
              class="font-weight-bold"
              style="
                white-space: normal;
                height: auto;
                padding: 6px 12px;
                text-align: center;
              "
            >
              <span class="d-none d-sm-inline"
                >TOTAL AMOUNT CLAIMED FOR THIS FILE:</span
              >&nbsp;<span class="d-inline"
                >RM
                {{
                  Number(claimTotal).toLocaleString("en", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }}</span
              >
            </v-chip>
          </v-col>
          <v-col
            :cols="section.id === 2 && claimTotal > 0 ? 4 : 6"
            class="d-flex justify-end align-center"
          >
            <v-btn
              v-if="shouldShowUploadButton(section.id)"
              outlined
              class="my-2 rounded-pill"
              color="primary"
              @click="openUploadDialog(section.id)"
            >
              <v-icon left>mdi-upload</v-icon>
              Upload
            </v-btn>
          </v-col>
        </v-row>
        <v-card
          v-if="section.filterFn(documents, casefile).length > 0"
          class="rounded-xl elevation-0 mt-2"
        >
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-left">File</th>
                <th class="text-left">Type</th>
                <th class="text-left">Remark</th>
                <th class="text-left">Uploaded By</th>
                <th class="text-left">Uploaded At</th>
                <th class="text-left"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="doc in section.filterFn(documents, casefile)"
                :key="doc.id"
                class="doc-row"
              >
                <td class="text-left">
                  <v-btn
                    outlined
                    x-small
                    block
                    color="primary"
                    class="rounded-pill"
                    @click="onClick(doc)"
                  >
                    <v-icon left small>mdi-file-download</v-icon>
                    {{ doc.name }}
                  </v-btn>
                </td>
                <td class="text-left">
                  <v-chip
                    small
                    color="primary lighten-4"
                    text-color="primary"
                    class="font-weight-bold"
                  >
                    {{ doc.Type }}
                  </v-chip>
                </td>
                <td class="text-left">{{ doc.Remark }}</td>
                <td
                  v-for="uploader in filterUser(doc.createdBy)"
                  :key="uploader.id"
                  class="text-left"
                >
                  <v-avatar size="24" class="mr-1" color="grey lighten-2">
                    <v-icon small color="grey">mdi-account</v-icon>
                  </v-avatar>
                  {{ uploader.username }}
                </td>
                <td class="text-left">{{ formatDate(doc.createdAt) }}</td>
                <td class="text-left">
                  <v-tooltip bottom transition="fab-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        color="red darken-1"
                        v-if="management"
                        class="doc-delete-icon"
                        @click="deleteDoc(doc)"
                      >
                        mdi-trash-can-outline
                      </v-icon>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
        <v-alert
          v-else
          type="info"
          border="left"
          color="primary"
          dense
          class="mt-2"
        >
          No documents uploaded yet.
        </v-alert>
      </div>
    </v-card>
  </div>
</template>
<script>
import DocsDataService from "../../services/DocsDataService";
import axios from "axios";
import UploadService from "../../services/UploadDocsService";
import MemberDataService from "../../services/MemberDataService";
import moment from "moment";
import { apiConfig } from "../../config/api.config";

export default {
  name: "documents",
  props: {
    casefile: {
      type: Object,
      required: true,
    },
    preloadedDocs: {
      // Use null as the default sentinel so the component can distinguish
      // between "prop not provided (component should fetch)" and
      // "parent provided an explicit empty array (parent will fetch)."
      type: Array,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    claimTotal: {
      type: [String, Number],
      default: 0,
    },
  },
  data() {
    return {
      currentFile: undefined,
      progress: 0,
      message: "",
      management: false,
      fileInfos: [],
      rules: [
        (value) =>
          !value ||
          value.size < 4000000 ||
          "File size should be less than 4 MB!",
      ],
      documents: this.preloadedDocs || [], // Initialize with preloaded docs (or empty)
      editedIndex: -1,
      editedItem: {
        id: null,
        caseId: null,
        docStageId: null,
        name: "",
        Type: "",
        Remark: "",
        createdBy: null,
      },
      members: [],
      selectTypes: [
        "Adjuster Report",
        "Court Documents",
        "Cover Note / Policy",
        "Employment letters",
        "Insurers Instruction",
        "JPJ Report",
        "Medical Report",
        "Other Claim Documents",
        "Police Documents (Full Set)",
        "Police Out-Come",
        "Police Reports",
        "Solicitors Instruction",
        "Statutory Declaration",
      ],
      uploads: {
        id: null,
        caseId: "",
        name: "",
        Type: "",
        Remark: "",
        createdBy: "",
      },
      dialogUploadDoc: false,
      currentStageId: null,
      dialogTitle: "",
      // dialogUploadDoc: false,

      dialogDeleteDoc: false,
      dialogDeleteDoc1: false,
      dialogDeleteDoc2: false,
      dialogDeleteDoc3: false,
      UploadAccess: false,
      adjUploadAccess: false,
      ediUploadAccess: false,
      fileInputKey: 0,
      docSections: [
        {
          id: 1,
          title: "INITIAL DOCUMENT(S)",
          bg: "red lighten-5",
          icon: "mdi-file-document-outline",
          iconColor: "red darken-2",
          avatarColor: "red lighten-4",
          filterFn: function (docs, casefile) {
            return docs.filter(
              (doc) => doc.caseId === casefile.id && doc.docStageId === 1,
            );
          },
        },
        {
          id: 2,
          title: "ADJUSTER'S REPORT(S)",
          bg: "orange lighten-5",
          icon: "mdi-account-tie",
          iconColor: "orange darken-2",
          avatarColor: "orange lighten-4",
          filterFn: function (docs, casefile) {
            return docs.filter(
              (doc) => doc.caseId === casefile.id && doc.docStageId === 2,
            );
          },
        },
        {
          id: 3,
          title: "EDITED REPORT(S)",
          bg: "yellow lighten-5",
          icon: "mdi-pencil",
          iconColor: "yellow darken-2",
          avatarColor: "yellow lighten-4",
          filterFn: function (docs, casefile) {
            return docs.filter(
              (doc) => doc.caseId === casefile.id && doc.docStageId === 3,
            );
          },
        },
        {
          id: 4,
          title: "FINAL REPORT(S)",
          bg: "green lighten-5",
          icon: "mdi-file-check-outline",
          iconColor: "green darken-2",
          avatarColor: "green lighten-4",
          filterFn: function (docs, casefile) {
            return docs.filter(
              (doc) => doc.caseId === casefile.id && doc.docStageId === 4,
            );
          },
        },
      ],
    };
  },

  computed: {
    isUploadDocDisabled() {
      return !this.uploads.Type;
    },
  },
  watch: {
    preloadedDocs: {
      immediate: true,
      handler(newDocs) {
        // Only update local documents when the parent provided an array
        // with contents. If the prop is null (not provided) we should
        // perform our own fetch instead.
        if (Array.isArray(newDocs) && newDocs.length > 0) {
          this.documents = newDocs;
        }
      },
    },
    uploadDoc(val) {
      val || this.closeUploadDoc();
    },
    uploadDoc1(val) {
      val || this.closeUploadDoc1();
    },
    uploadDoc2(val) {
      val || this.closeUploadDoc2();
    },
    uploadDoc3(val) {
      val || this.closeUploadDoc3();
    },
    deleteDoc(val) {
      val || this.closeDeleteDoc();
    },
  },
  methods: {
    checkUploadAccess() {
      if (
        this.$store.state.auth.user.roles.includes(`ADMIN`) ||
        this.$store.state.auth.user.roles.includes(`MANAGER`) ||
        this.$store.state.auth.user.roles.includes(`BRANCHCLERK`) ||
        this.$store.state.auth.user.roles.includes(`BRANCHMANAGER`) ||
        this.$store.state.auth.user.roles.includes(`CLERK`)
      ) {
        this.UploadAccess = true;
      }
    },
    checkAdjUploadAccess() {
      if (
        this.$store.state.auth.user.roles.includes(`ADMIN`) ||
        this.$store.state.auth.user.roles.includes(`MANAGER`) ||
        this.$store.state.auth.user.roles.includes(`BRANCHCLERK`) ||
        this.$store.state.auth.user.roles.includes(`BRANCHMANAGER`) ||
        this.$store.state.auth.user.roles.includes(`ADJUSTER`) ||
        this.$store.state.auth.user.roles.includes(`CLERK`)
      ) {
        this.adjUploadAccess = true;
      }
    },
    checkEdiUploadAccess() {
      if (
        this.$store.state.auth.user.roles.includes(`ADMIN`) ||
        this.$store.state.auth.user.roles.includes(`MANAGER`) ||
        this.$store.state.auth.user.roles.includes(`BRANCHCLERK`) ||
        this.$store.state.auth.user.roles.includes(`BRANCHMANAGER`) ||
        this.$store.state.auth.user.roles.includes(`EDITOR`) ||
        this.$store.state.auth.user.roles.includes(`CLERK`)
      ) {
        this.ediUploadAccess = true;
      }
    },
    checkManagement() {
      if (
        this.$store.state.auth.user.roles.includes(`ADMIN`) ||
        this.$store.state.auth.user.roles.includes(`MANAGER`)
      ) {
        this.management = true;
      }
    },
    async onClick(doc) {
      // Request a short-lived signed download token from the API, then
      // fetch the file via XHR so we can surface JSON error messages via snackbar
      // instead of the browser navigating to a JSON error page.
      try {
        const res = await DocsDataService.getDownloadToken(doc.id);
        const token = res && res.data && res.data.token;
        // Prefer the suggested downloadName returned by the token endpoint
        const suggestedDownloadName = res && res.data && res.data.downloadName;
        if (!token) throw new Error("No download token received");

        const downloadUrl = `${
          apiConfig.docsURL
        }/download?token=${encodeURIComponent(token)}`;

        // Try to GET the file as a blob. If the server responds with an error
        // (JSON), axios will throw and we will extract the error message to show
        // in the snackbar. Use responseType 'blob' so successful file data is
        // returned as a Blob we can download programmatically.
        const downloadResp = await axios.get(downloadUrl, {
          responseType: "blob",
          // allow 200-399 as success; treat 4xx/5xx as errors
          validateStatus: function (status) {
            return status >= 200 && status < 400; // Resolve only on success
          },
        });

        // Determine filename: prefer server-suggested downloadName, then
        // Content-Disposition header, then the doc.name.
        let filename = suggestedDownloadName || doc.name;
        const cd =
          downloadResp.headers && downloadResp.headers["content-disposition"];
        if (cd) {
          const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]*)"?/i);
          if (match && match[1]) filename = decodeURIComponent(match[1]);
        }

        // Create object URL and trigger download
        const blob = new Blob([downloadResp.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || doc.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        // Attempt to extract JSON error message from various response shapes
        let message = "Failed to start download";
        console.error("download error", err);
        try {
          const data = err && err.response && err.response.data;
          if (data) {
            // If server returned a Blob (common when responseType is 'blob'),
            // read it first and attempt to parse JSON out of it.
            if (data instanceof Blob) {
              const text = await new Response(data).text();
              try {
                const obj = JSON.parse(text);
                message = obj.message || text;
              } catch (e) {
                message = text || message;
              }
            } else if (typeof data === "object" && data !== null) {
              // axios may have already parsed JSON into an object
              message = data.message || JSON.stringify(data);
            } else if (typeof data === "string") {
              // plain string
              try {
                const obj = JSON.parse(data);
                message = obj.message || data;
              } catch (e) {
                message = data;
              }
            } else {
              message = String(data);
            }
          } else if (err && err.message) {
            message = err.message;
          }
        } catch (e) {
          // ignore parsing errors
        }

        // Ensure we always show a useful, non-empty string in the snackbar
        if (!message) {
          const status = err && err.response && err.response.status;
          message = `Download failed (${status || "error"})`;
        }

        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "error",
          text: String(message),
        });
      }
    },
    moment: function (date) {
      return moment(date);
    },
    formatDate: function (date) {
      return moment(date).format("DD/MM/YYYY, h:mm:ss a");
    },
    refreshList() {
      this.retrieveDocs();
    },
    filterCaseFile() {
      return this.casefile.filter((casef) => casef.id === this.casefile.id);
    },
    filterCase() {
      return this.documents.filter(
        (doc) => doc.caseId === this.casefile.id && doc.docStageId === 1,
      );
    },
    filterCase1() {
      return this.documents.filter(
        (doc) => doc.caseId === this.casefile.id && doc.docStageId === 2,
      );
    },
    filterCase2() {
      return this.documents.filter(
        (doc) => doc.caseId === this.casefile.id && doc.docStageId === 3,
      );
    },
    filterCase3() {
      return this.documents.filter(
        (doc) => doc.caseId === this.casefile.id && doc.docStageId === 4,
      );
    },
    filterUser(item) {
      return this.members.filter((member) => member.id === item);
    },
    selectFile(file) {
      this.currentFile = file;
    },
    onFileInputChange(event) {
      const files = event.target.files;
      if (files && files.length > 0) {
        this.selectFile(files[0]);
      }
    },
    retrieveDocs() {
      // Request documents scoped to the provided caseId so we never fetch the
      // entire documents table unintentionally. Use the service's findByCase
      // which supports server-side filtering. Preserve existing response
      // normalization (array or {data,totalCount}).
      DocsDataService.findByCase(this.casefile.id)
        .then((response) => {
          try {
            if (response && response.data) {
              if (Array.isArray(response.data)) {
                this.documents = response.data;
                this.$emit("documents-count", this.documents.length);
                return;
              }
              if (Array.isArray(response.data.data)) {
                this.documents = response.data.data;
                this.$emit(
                  "documents-count",
                  response.data.totalCount || this.documents.length,
                );
                return;
              }
            }
            // Fallback: treat as empty array
            this.documents = [];
            this.$emit("documents-count", 0);
          } catch (e) {
            console.error(
              "documents.retrieveDocs: failed to normalize response",
              e,
            );
            this.documents = [];
            this.$emit("documents-count", 0);
          }
        })
        .catch((e) => {
          console.error("documents.retrieveDocs: fetch failed", e);
          this.documents = [];
          this.$emit("documents-count", 0);
        });
    },
    retrieveUser() {
      MemberDataService.getAll()
        .then((response) => {
          this.members = response.data;
        })
        .catch((e) => {
          console.log(e);
        });
    },

    deleteDoc(item) {
      this.editedItem = Object.assign({}, item);
      this.dialogDeleteDoc = true;
    },

    closeDeleteDoc() {
      this.dialogDeleteDoc = false;
      this.$nextTick(() => {});
    },
    closeDeleteDoc1() {
      this.dialogDeleteDoc1 = false;
      this.$nextTick(() => {});
    },
    closeDeleteDoc2() {
      this.dialogDeleteDoc2 = false;
      this.$nextTick(() => {});
    },
    closeDeleteDoc3() {
      this.dialogDeleteDoc3 = false;
      this.$nextTick(() => {});
    },
    deleteItemConfirm() {
      DocsDataService.delete(this.editedItem.id, this.editedItem.name)
        .then((response) => {
          this.$store.dispatch("setSnackbar", {
            showing: true,
            color: "error",
            text:
              (response && response.data && response.data.message) ||
              toString(),
          });
          this.refreshList();
          // Emit event to parent to reload documents after delete
          this.$emit("document-uploaded");
        })
        .catch((e) => {
          console.log(e);
        });

      this.closeDeleteDoc();
    },
    triggerFileInput() {
      // Reset input value so selecting same file triggers change
      this.$refs.fileInput.value = null;
      this.$refs.fileInput.click();
    },
    openUploadDialog(stageId) {
      this.currentStageId = stageId;
      this.dialogUploadDoc = true;
      this.currentFile = undefined; // Reset selected file
      this.fileInputKey++; // Force input re-render

      // Set dialog title based on stage
      switch (stageId) {
        case 1:
          this.dialogTitle = "UPLOAD INITIAL DOCUMENT";
          break;
        case 2:
          this.dialogTitle = "UPLOAD ADJUSTER'S REPORT";
          break;
        case 3:
          this.dialogTitle = "UPLOAD EDITED REPORT";
          break;
        case 4:
          this.dialogTitle = "UPLOAD FINAL REPORT";
          break;
      }
    },
    shouldShowUploadButton(stageId) {
      switch (stageId) {
        case 1:
          return this.UploadAccess;
        case 2:
          return this.adjUploadAccess;
        case 3:
          return this.ediUploadAccess;
        case 4:
          return this.UploadAccess;
        default:
          return false;
      }
    },

    closeUploadDialog() {
      this.dialogUploadDoc = false;
      this.currentStageId = null;
      this.currentFile = undefined; // Reset selected file
      this.fileInputKey++; // Force input re-render
      this.$nextTick(() => {});
    },

    uploadDocConfirm(stageId) {
      if (!this.currentFile) {
        this.$store.dispatch("setSnackbar", {
          showing: true,
          color: "warning",
          text: "Please select a file!",
        });
        return;
      }

      // Create form data with additional metadata
      const formData = new FormData();
      formData.append("file", this.currentFile);
      // Add metadata to identify this specific upload
      formData.append("caseId", this.casefile.id);
      formData.append("stageId", stageId);
      formData.append("userId", this.$store.state.auth.user.id);
      formData.append("type", this.uploads.Type);
      formData.append("remark", this.uploads.Remark);

      this.message = "";
      UploadService.upload(formData, (event) => {
        this.progress = Math.round((100 * event.loaded) / event.total);
      })
        .then((response) => {
          const uploadedFile = response.data.name;
          const data = {
            caseId: this.casefile.id,
            docStageId: stageId,
            name: uploadedFile,
            Type: this.uploads.Type,
            Remark: this.uploads.Remark,
            createdBy: this.$store.state.auth.user.id,
          };
          this.$store.dispatch("setSnackbar", {
            showing: true,
            color: "success",
            text:
              (response && response.data && response.data.message) ||
              toString(),
          });
          return DocsDataService.create(data);
        })
        .then((response) => {
          this.closeUploadDialog();
          this.$store.dispatch("setSnackbar", {
            showing: true,
            color: "success",
            text:
              (response && response.data && response.data.message) ||
              toString(),
          });
          this.uploads.Type = "";
          this.uploads.Remark = "";
          this.refreshList();
          this.currentFile = undefined;
          // Emit event to parent to reload documents
          this.$emit("document-uploaded");
        })
        .catch((e) => {
          this.$store.dispatch("setSnackbar", {
            showing: true,
            color: "warning",
            text:
              (e.response && e.response.data && e.response.data.message) ||
              e.message ||
              toString(),
          });
        });
    },
  },
  mounted() {
    // If parent did not provide preloadedDocs (prop left unset -> null),
    // perform our own fetch. When the parent passes an explicit array
    // (even empty) it intends to manage loading itself and we must not
    // duplicate the request (avoids race conditions where both parent and
    // child fetch and emit counts).
    if (this.preloadedDocs === null) {
      this.retrieveDocs();
    }
    this.retrieveUser();
    this.checkManagement();
    this.checkUploadAccess();
    this.checkAdjUploadAccess();
    this.checkEdiUploadAccess();
  },
};
</script>
<style scoped>
.upload-drop-area {
  border: 2px dashed #90caf9;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  background: #e3f2fd;
  transition: background 0.2s, border-color 0.2s;
}
.upload-drop-area:hover {
  background: #bbdefb;
  border-color: #1976d2;
}
.doc-row:hover {
  background: #f0f4ff;
  transition: background 0.2s;
}
.doc-delete-icon {
  cursor: pointer;
  transition: color 0.2s;
}
.doc-delete-icon:hover {
  color: #d32f2f !important;
}
.v-card.rounded-xl {
  border-radius: 18px !important;
}
.v-btn.rounded-pill {
  border-radius: 999px !important;
}
</style>
